/**
 * MessageInput - Text input with GIF button + send button.
 * Shows reply indicator when replying to a message.
 */

import { useState, useRef, useCallback, lazy, Suspense } from 'react'
import { useGroupChat } from '../GroupChatContext'
import { useAuth } from '../../../features/auth'
import type { GifAttachment } from '../../../features/messaging/gif/types'

// Lazy load GIF picker
const GifPicker = lazy(() =>
  import('../../../features/messaging/gif/GifPicker').then(m => ({ default: m.GifPicker }))
)

export function MessageInput() {
  const { sendMessage, sendGif, isConnected, startTyping, stopTyping, replyingTo, setReplyingTo } = useGroupChat()
  const { user } = useAuth()
  const [text, setText] = useState('')
  const [showGifPicker, setShowGifPicker] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSend = useCallback(() => {
    const trimmed = text.trim()
    if (!trimmed || !isConnected) return
    sendMessage(trimmed)
    setText('')
    stopTyping()
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
  }, [text, isConnected, sendMessage, stopTyping])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    if (value.length <= 500) {
      setText(value)
      startTyping()
      const el = e.target
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 100) + 'px'
    }
  }

  const handleGifSelect = useCallback((gif: GifAttachment) => {
    sendGif(gif.url, gif.thumbnail_url, gif.filename)
    setShowGifPicker(false)
  }, [sendGif])

  return (
    <div className="flex-shrink-0 bg-white border-t border-pink-200">
      {/* Reply indicator */}
      {replyingTo && (
        <div className="flex items-center justify-between px-4 py-2 bg-pink-50 border-b border-pink-100">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-0.5 h-8 bg-pink-400 rounded-full flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-pink-600">Replying to {replyingTo.username}</p>
              <p className="text-xs text-gray-500 truncate">{replyingTo.text}</p>
            </div>
          </div>
          <button onClick={() => setReplyingTo(null)} className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      <div className="px-3 py-2">
        <div className="flex items-end gap-2 max-w-full">
          {/* GIF button */}
          <button
            onClick={() => setShowGifPicker(true)}
            disabled={!isConnected}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-pink-500 hover:bg-pink-50 transition-colors disabled:opacity-30"
            title="Send GIF"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.5 9H13v6h-1.5zM9 9H6c-.6 0-1 .5-1 1v4c0 .5.4 1 1 1h3c.6 0 1-.5 1-1v-2H8.5v1.5h-2v-3H10V10c0-.5-.4-1-1-1zm10 1.5V9h-4.5v6H16v-2h2v-1.5h-2v-1z"/>
            </svg>
          </button>

          {/* Text input */}
          <div className="flex-1 min-w-0 relative">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={isConnected ? 'Type a message...' : 'Connecting...'}
              disabled={!isConnected}
              rows={1}
              className="w-full bg-pink-50 border border-pink-200 rounded-2xl px-3 py-2 text-gray-800 text-[16px] placeholder-gray-400 resize-none focus:outline-none focus:border-pink-400 transition-colors disabled:opacity-50"
              style={{ maxHeight: '100px' }}
            />
            {text.length > 400 && (
              <span className={`absolute right-3 bottom-1.5 text-[10px] ${text.length >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                {text.length}/500
              </span>
            )}
          </div>

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={!text.trim() || !isConnected}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-white transition-all disabled:opacity-30"
            style={{ backgroundColor: '#ec4899' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* GIF Picker Modal */}
      {showGifPicker && (
        <Suspense fallback={null}>
          <GifPicker
            isOpen={showGifPicker}
            onClose={() => setShowGifPicker(false)}
            onSelect={handleGifSelect}
            userId={user?.id || 'anonymous'}
          />
        </Suspense>
      )}
    </div>
  )
}
